import { useEffect, useState } from 'react';
import { CANCEL_JOB_ACTION } from '~/actions';
import { Pagination } from '~/commonComponents';
import {
  DashboardJobsType,
  IDashboardJob,
  useDashboardJobInfo,
  useGetDashboardJobs,
} from '~/domain/DashboardJobs';
import { useIsSuccess } from '~/hooks';
import CustomSpinner from '../../../../commonComponents/CustomSpinner';
import { Table } from '../../../../commonComponents/Table/Table';
import { EmptyMessage } from './EmptyMessage';

interface IProps {
  type: DashboardJobsType;
  emptyMessageType?: string;
  renderRow: (item: IDashboardJob) => React.ReactNode;
  headerTitles: string[];
  onRowClick: (item: IDashboardJob) => void;
  dataCy?: string;
  rowDataCy?: string;
}

const successActions = [CANCEL_JOB_ACTION];

export const CustomTable = ({
  type,
  emptyMessageType,
  renderRow,
  headerTitles,
  onRowClick,
  dataCy,
  rowDataCy,
}: IProps) => {
  const dashboardInfo = useDashboardJobInfo();
  const [page, setPage] = useState(1);
  const { data, isPending, isLoading, isFetching, isRefetching, refetch } = useGetDashboardJobs({
    type,
    page,
    limit: 15,
  });
  const { isSuccess } = useIsSuccess(successActions);

  let totalPages = 0;

  if (type === DashboardJobsType.OPEN) {
    totalPages = Math.ceil((dashboardInfo?.openCount ?? 0) / 15);
  } else if (type === DashboardJobsType.ACTION_REQUIRED) {
    totalPages = Math.ceil((dashboardInfo?.pendingCount ?? 0) / 15);
  } else if (type === DashboardJobsType.FILLED) {
    totalPages = Math.ceil((dashboardInfo?.filledCount ?? 0) / 15);
  }

  useEffect(() => {
    if (isSuccess) {
      refetch();
    }
  }, [isSuccess, refetch]);

  if (isLoading || isPending || isFetching || isRefetching) {
    return <CustomSpinner isLoading size={32} />;
  }

  return (
    <div>
      {!data?.length && <EmptyMessage type={emptyMessageType ?? type} />}

      {data?.length > 0 && (
        <Table
          data={data}
          dataKey="id"
          renderRow={renderRow}
          headerTitles={headerTitles}
          showActions
          onRowClick={onRowClick}
          isLoading={isLoading}
          dataCy={dataCy}
          rowDataCy={rowDataCy}
          PaginationComponent={() => (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              maxButtons={5}
            />
          )}
        />
      )}
    </div>
  );
};
